import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Search, Package, Home, Building, Wrench, Palette, Car, Zap, Cpu, ShoppingCart, Heart, Star, Settings, Plus } from 'lucide-react';
import * as Icons from 'lucide-react';

interface IconSelectorProps {
  value?: string;
  onValueChange: (iconName: string) => void;
  label?: string;
}

// Basic category icons
const BASIC_ICONS = ['Home', 'Package', 'Building', 'User', 'Settings', 'Search', 'Menu', 'Plus', 'Edit', 'Trash', 'Eye', 'Save', 'Download', 'Upload', 'Mail', 'Phone', 'Globe', 'Lock', 'Unlock', 'Key', 'Shield', 'Bell', 'Calendar', 'Clock', 'Map', 'Camera', 'Image', 'Video', 'Music', 'Book', 'File', 'Folder', 'Archive', 'Database', 'Cloud', 'Server', 'Heart', 'Star', 'Check', 'X'];

// Navigation icons
const NAVIGATION_ICONS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ChevronUp', 'ChevronDown', 'ChevronLeft', 'ChevronRight', 'MoreHorizontal', 'MoreVertical', 'Navigation', 'Compass', 'MapPin', 'Route', 'Locate', 'Crosshair', 'CornerUpRight', 'CornerUpLeft', 'CornerDownRight', 'CornerDownLeft', 'Move', 'Forward', 'Backward', 'SkipForward', 'SkipBack', 'Undo', 'Redo', 'Expand', 'Shrink', 'Maximize', 'Minimize', 'RotateCw', 'RotateCcw', 'Flip', 'Drag', 'Drop', 'Grab', 'Hand', 'Pointer'];

// Technology icons
const TECH_ICONS = ['Laptop', 'Monitor', 'Smartphone', 'Tablet', 'Wifi', 'Bluetooth', 'Battery', 'Power', 'Cpu', 'Zap', 'Radio', 'Tv', 'Projector', 'Headphones', 'Speaker', 'Volume', 'VolumeX', 'Volume1', 'Volume2', 'Mic', 'Play', 'Pause', 'Stop', 'Record', 'Film', 'Photo', 'Aperture', 'Focus', 'Zoom', 'ZoomIn', 'ZoomOut', 'Print', 'Scan', 'Copy', 'Cut', 'Paste', 'Lightbulb', 'Sun', 'Moon'];

// Business icons  
const BUSINESS_ICONS = ['Building2', 'Store', 'Shop', 'ShoppingBag', 'ShoppingCart', 'Basket', 'Truck', 'Package2', 'Warehouse', 'Factory', 'Office', 'Bank', 'Hotel', 'Restaurant', 'CreditCard', 'DollarSign', 'Coins', 'Percent', 'Calculator', 'Chart', 'BarChart', 'PieChart', 'TrendingUp', 'TrendingDown', 'Activity', 'Target', 'Flag', 'Trophy', 'Award', 'Medal', 'Crown', 'Gem', 'Diamond', 'Users', 'UserPlus', 'UserMinus', 'UserCheck', 'Briefcase', 'Handshake'];

// Medical & Nature icons
const MEDICAL_NATURE_ICONS = ['Hospital', 'Cross', 'Pill', 'Stethoscope', 'Thermometer', 'Syringe', 'Bandage', 'Leaf', 'Tree', 'Flower', 'Droplets', 'Flame', 'Wind', 'Umbrella', 'Snowflake', 'Apple', 'Cherry', 'Grape', 'Lemon', 'Carrot', 'Coffee', 'Wine', 'Beer', 'Fish', 'Bug', 'Bird', 'Cat', 'Dog', 'Rabbit', 'Turtle', 'Butterfly', 'Feather', 'Pawprint', 'Bone', 'Egg', 'Sprout', 'Seedling', 'Cactus', 'Mushroom'];

// Transport & Travel icons
const TRANSPORT_ICONS = ['Car', 'Plane', 'Train', 'Bus', 'Bike', 'Boat', 'Rocket', 'Truck', 'Ship', 'Helicopter', 'Anchor', 'Compass', 'Luggage', 'Ticket', 'Passport', 'MapPin', 'Globe', 'Mountain', 'Tent', 'Camera', 'Binoculars', 'Backpack', 'Suitcase', 'Beach', 'Palmtree', 'Island', 'Waves', 'Bridge', 'Castle', 'Church', 'Statue', 'Museum', 'School', 'University', 'Stadium', 'Theater', 'Tower', 'Landmark'];

// Tools & Construction icons
const TOOLS_ICONS = ['Hammer', 'Wrench', 'Screwdriver', 'Drill', 'Saw', 'Scissors', 'Ruler', 'Tool', 'Crane', 'Construction', 'Cone', 'Barrier', 'Warning', 'HardHat', 'Paintbrush', 'Brush', 'Palette', 'Pipette', 'Ink', 'Pencil', 'Pen', 'Eraser', 'Marker', 'Crayon', 'Clipboard', 'Calculator', 'Paperclip', 'Pin', 'Pushpin', 'Thumbtack', 'Magnet', 'Chain', 'Link', 'Unlink', 'Anchor', 'Lock', 'Unlock', 'Key'];

// All icon categories
const ICON_CATEGORIES = {
  basic: { name: 'בסיסי', icons: BASIC_ICONS },
  navigation: { name: 'ניווט', icons: NAVIGATION_ICONS },
  tech: { name: 'טכנולוגיה', icons: TECH_ICONS },
  business: { name: 'עסקים', icons: BUSINESS_ICONS },
  medical: { name: 'רפואה וטבע', icons: MEDICAL_NATURE_ICONS },
  transport: { name: 'תחבורה ונסיעות', icons: TRANSPORT_ICONS },
  tools: { name: 'כלים ובנייה', icons: TOOLS_ICONS },
};

const IconSelector = ({ value, onValueChange, label = "בחר אייקון" }: IconSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('basic');

  const currentIcons = ICON_CATEGORIES[selectedCategory as keyof typeof ICON_CATEGORIES]?.icons || [];
  const filteredIcons = currentIcons.filter(iconName => 
    iconName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderIcon = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName];
    if (!IconComponent) return null;
    return <IconComponent className="h-6 w-6" />;
  };

  const handleSelect = (iconName: string) => {
    onValueChange(iconName);
    setIsOpen(false);
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="w-full justify-start gap-2">
            {value ? (
              <>
                {renderIcon(value)}
                <span>{value}</span>
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                <span>בחר אייקון</span>
              </>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh]" dir="rtl">
          <DialogHeader>
            <DialogTitle>בחירת אייקון</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search and Category Filter */}
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="חפש אייקון..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pr-10"
                  />
                </div>
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ICON_CATEGORIES).map(([key, category]) => (
                    <SelectItem key={key} value={key}>{category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Current Selection */}
            {value && (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground">נבחר:</span>
                {renderIcon(value)}
                <Badge variant="secondary">{value}</Badge>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    onValueChange('');
                    setIsOpen(false);
                  }}
                  className="mr-auto"
                >
                  הסר
                </Button>
              </div>
            )}

            {/* Icons Grid */}
            <ScrollArea className="h-96">
              <div className="grid grid-cols-6 gap-2 p-2">
                {filteredIcons.map((iconName) => (
                  <Button
                    key={iconName}
                    variant={value === iconName ? "default" : "ghost"}
                    className="h-16 w-full flex flex-col items-center justify-center gap-1 p-2"
                    onClick={() => handleSelect(iconName)}
                  >
                    {renderIcon(iconName)}
                    <span className="text-xs text-center leading-tight">
                      {iconName}
                    </span>
                  </Button>
                ))}
              </div>
              {filteredIcons.length === 0 && (
                <div className="text-center text-muted-foreground py-8">
                  לא נמצאו אייקונים התואמים לחיפוש
                </div>
              )}
            </ScrollArea>

            {/* Statistics */}
            <div className="text-xs text-muted-foreground text-center">
              מציג {filteredIcons.length} מתוך {currentIcons.length} אייקונים בקטגוריה "{ICON_CATEGORIES[selectedCategory as keyof typeof ICON_CATEGORIES]?.name}"
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IconSelector;